import { useMutation } from '@tanstack/react-query';

import { useSupabase } from './use-supabase';

interface PhoneCredentials {
  phone: string;
  captchaToken?: string;
  shouldCreateUser?: boolean;
  name?: string;
  email?: string;
  // Slug of the tenant the user is currently on. Forwarded as
  // `current_tenant_slug` in `options.data` so the Send-SMS hook can resolve
  // tenant branding for the OTP email/WhatsApp delivery — needed for both
  // sign-in (recovery) and sign-up flows, since the user's stored
  // `tenant_slug` may be missing or stale.
  tenantSlug?: string;
  // Role to stamp on a NEWLY created account (read by kit.setup_new_user from
  // metadata). The phone-first sign-up passes 'guest' so the bare account
  // created at OTP-send time stays a guest until the post-OTP profile step
  // promotes it to 'user'. Without this the trigger defaults to 'user' and an
  // abandoned profile would look like a complete account.
  signupRole?: string;
}

export function useSignInWithPhone() {
  const client = useSupabase();
  const mutationKey = ['auth', 'sign-in-with-phone'];

  const mutationFn = async (credentials: PhoneCredentials) => {
    const {
      phone,
      captchaToken,
      shouldCreateUser,
      name,
      email,
      tenantSlug,
      signupRole,
    } = credentials;

    // Build options.data from any context we want to thread into the
    // Send-SMS hook. `tenantSlug` rides along on every call; name/email/role
    // are only meaningful on sign-up.
    const data: Record<string, string> = {};
    if (tenantSlug) data.current_tenant_slug = tenantSlug;
    if (shouldCreateUser && name) data.name = name;
    if (shouldCreateUser && email && email.length > 0) data.email = email;
    if (shouldCreateUser && signupRole) data.role = signupRole;

    const result = await client.auth.signInWithOtp({
      phone,
      options: {
        captchaToken,
        shouldCreateUser,
        channel: 'sms', // Uses our Send SMS hook -> Authentica API
        data: Object.keys(data).length > 0 ? data : undefined,
      },
    });

    if (result.error) {
      if (shouldIgnoreError(result.error.message)) {
        console.warn(
          `Ignoring error during development: ${result.error.message}`,
        );

        return {} as never;
      }

      throw result.error.message;
    }

    return result.data;
  };

  return useMutation({
    mutationFn,
    mutationKey,
  });
}

function shouldIgnoreError(error: string) {
  return isSmsProviderNotSetupError(error);
}

function isSmsProviderNotSetupError(error: string) {
  return error.includes(`sms Provider could not be found`);
}
